import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, Check } from 'lucide-react';
import { Header } from '../components/Header';

const AnwaltSetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [lawyerInfo, setLawyerInfo] = useState(null);

  useEffect(() => {
    const t = searchParams.get('token');
    if (!t) {
      setError('Kein Token gefunden');
      setVerifying(false);
      return;
    }
    setToken(t);
    verifyToken(t);
  }, [searchParams]);

  const verifyToken = async (t) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/anwalt/verify-token/${t}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Ungültiger Link');
      }

      setLawyerInfo(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/anwalt/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Fehler beim Setzen des Passworts');
      }

      // Store token
      localStorage.setItem('lawyer_token', data.token);
      localStorage.setItem('lawyer_id', data.lawyer.id);
      localStorage.setItem('lawyer_email', data.lawyer.email);
      localStorage.setItem('lawyer_name', data.lawyer.name);

      // Redirect
      navigate('/anwalt/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p>Verifiziere Link...</p>
      </div>
    );
  }

  if (error && !lawyerInfo) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="pt-32 pb-16">
          <div className="max-w-md mx-auto px-6">
            <div className="bg-white border-2 border-red-200 rounded-3xl p-10 text-center">
              <h1 className="text-2xl font-bold text-red-700 mb-4">Ungültiger Link</h1>
              <p className="text-gray-600 mb-6">{error}</p>
              <a href="/anwalt/login" className="text-brand-green hover:text-brand-green-dark font-semibold underline">
                Zum Login
              </a>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white border-2 border-gray-100 rounded-3xl p-10 shadow-xl">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-brand-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-brand-green" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Passwort setzen</h1>
              <p className="text-gray-600">Hallo {lawyerInfo?.name}</p>
              <p className="text-sm text-gray-500 mt-1">{lawyerInfo?.law_firm}</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Neues Passwort *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green"
                    placeholder="Mindestens 8 Zeichen"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Passwort bestätigen *</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green"
                  placeholder="Passwort wiederholen"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-green hover:bg-brand-green-dark text-white py-4 rounded-xl font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              >
                {loading ? 'Speichern...' : 'Passwort setzen & Anmelden'}
                <Check className="w-5 h-5" />
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                <a href="/anwalt/login" className="text-brand-green hover:text-brand-green-dark font-semibold underline">
                  Zum Login
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AnwaltSetPasswordPage;
